import {
  addEffect,
  addHandlers,
  addProps,
  addState,
  flowMax,
  SimplePropsAdder,
  SimpleUnchangedProps,
} from 'ad-hok'
import {
  addEffectOnMount,
  branchIfNullish,
  getContextHelpers,
  toObjectKeys,
} from 'ad-hok-utils'
import { isLeft } from 'fp-ts/Either'
import * as ioTs from 'io-ts'
import { PathReporter } from 'io-ts/PathReporter'

import { addNavigate } from '../utils/addNavigate'
import { addAuthContext, authenticate } from '../utils/auth'
import { getEnvVar } from '../utils/env'
import { formatDateIso } from '../utils/formatting'
import { typedAs } from '../utils/typedAs'
import {
  AffiliateDomainCoder,
  AffiliateDomainInputCoder,
  BehindTheGateViewInput,
  BehindTheGateViewInputCoder,
  Bookmark,
  BookmarkCoder,
  BookmarkInput,
  BookmarkInputCoder,
  BrandCoder,
  BrandFeatureCoder,
  BrandFeatureInputCoder,
  BrandFeatureWithUsernameAndImageCoder,
  BrandFeedCoder,
  BrandInput,
  BrandInputCoder,
  BrandProductStatsCoder,
  CategoryFeedCoder,
  CategoryInputCoder,
  CategoryWithUserIdsCoder,
  CmsUserCoder,
  Config,
  ConfigCoder,
  CollegeCoder,
  CreatorEarningsCoder,
  CreatorEarningsCreatorOverviewCoder,
  CreatorEarningsDateInput,
  CreatorEarningsDateInputCoder,
  CreatorEarningsManyInput,
  CreatorEarningsManyInputCoder,
  CreatorEarningsOverviewCoder,
  CreatorFeatureCoder,
  CreatorFeatureInputCoder,
  CreatorFeatureWithUsernameAndImageCoder,
  CreatorFundCosts,
  CreatorFundCostsCoder,
  CreatorFundCostsInput,
  CreatorFundCostsInputCoder,
  CreatorLaunchImageCoder,
  CreatorLaunchImageInputCoder,
  CreatorLaunchImageWithImageCoder,
  CurrentUser,
  CurrentUserCoder,
  CustomLinkCoder,
  CustomLinkInputCoder,
  DetectedProductCoder,
  EditCoder,
  EditFeedViewInput,
  EditFeedViewInputCoder,
  EditInputCoder,
  EmailSource,
  FeaturedCategoriesCoder,
  FeaturedUsersFeedCoder,
  FollowCoder,
  FollowInputCoder,
  ImageCoder,
  ImageInput,
  ImageInputCoder,
  ImagePresignedUrlCoder,
  ImagePresignedUrlInput,
  ImagePresignedUrlInputCoder,
  InsightsCoder,
  IsAffiliatedProduct,
  IsAffiliatedProductCoder,
  IsAffiliatedProductInputCoder,
  isImageMediaContent,
  LabelCoder,
  LabelFeedCoder,
  LabelInputCoder,
  LoginCoder,
  LoginInput,
  LoginInputCoder,
  MediaCoder,
  MediaContent,
  MediaInputCoder,
  MediaQueueCoder,
  MediaViewInput,
  MediaViewInputCoder,
  MessageInput,
  MessageInputCoder,
  OAuthLoginUrlCoder,
  OutgoingClickInput,
  OutgoingClickInputCoder,
  OutgoingProductClickInput,
  OutgoingProductClickInputCoder,
  ProductDetailsCoder,
  ProductFeatureCoder,
  ProductFeatureInputCoder,
  ProductInputCoder,
  ProductSuggestionResponseCoder,
  ProductUrlCoder,
  ProductViewInput,
  ProductViewInputCoder,
  ProductWithImageAndRelatedProductIdsCoder,
  ProductWithImageCoder,
  QueuedImageCoder,
  QueuedImageInputCoder,
  RedemptionRequest,
  RedemptionRequestCoder,
  RedemptionRequestInput,
  RedemptionRequestInputCoder,
  Roundup,
  RoundupCoder,
  SearchResultClickInput,
  SearchResultClickInputCoder,
  SearchResultCoder,
  TagCoder,
  TagFeedCoder,
  TagInputCoder,
  UserEditCoder,
  UserEditFeedCoder,
  UserEditInput,
  UserEditInputCoder,
  UserEditPublicCoder,
  UserFeedCoder,
  UserInputCoder,
  UserListCoder,
  UserListFeedCoder,
  UserListInputCoder,
  UserListProductsInput,
  UserListProductsInputCoder,
  UserListPublicCoder,
  UserListViewInput,
  UserListViewInputCoder,
  UsersWithImagesCoder,
  UserWithImagesCoder,
  UsernameValidationCoder,
  VerificationStatusCoder,
  VerifyEmailInput,
  VerifyEmailInputCoder,
  VideoCoder,
  VideoInput,
  VideoInputCoder,
  VideoPlaybackIdCoder,
  VideoPresignedUrlCoder,
  FeedMediaCoder,
} from './types'

const queryParamsForDateRange = (
  start: Date | undefined,
  end: Date | undefined,
  includeStats: boolean
): URLSearchParams => {
  const queryParams = new URLSearchParams()

  if (start) {
    queryParams.append('start_date', formatDateIso(start))
  }

  if (end) {
    queryParams.append('end_date', formatDateIso(end))
  }

  queryParams.append('include_stats', includeStats.toString())

  return queryParams
}

export interface Resource<TModel, TInput> {
  get: (id: number) => Promise<TModel>
  getAll: () => Promise<TModel[]>
  getAllWithDateRange?: (
    start: Date | undefined,
    end: Date | undefined,
    includeStats: boolean
  ) => Promise<TModel[]>
  create: (input: TInput) => Promise<TModel>
  update: (id: number, input: TInput) => Promise<TModel>
  delete: (id: number) => Promise<void>
  search?: (query: string) => Promise<TModel[]>
  exportCsv?: () => Promise<Response>
}

const API_ROOT = getEnvVar('API_ROOT')

const makeApi = () => {
  const headers = async () => {
    const baseHeaders = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    }

    return { ...baseHeaders }
  }

  const fetchFromApi = async (
    path: string,
    options?: { method?: string; body?: any }
  ): Promise<Response> =>
    await fetch(`${API_ROOT}${path}`, {
      credentials: 'include',
      method: options?.method,
      headers: await headers(),
      body: options?.body ? JSON.stringify(options.body) : undefined,
    })

  const fetchAndDecodeFromApi = async <TCoder extends ioTs.Type<any>>(
    path: string,
    coder: TCoder,
    options?: { method?: string; body?: any }
  ): Promise<ioTs.TypeOf<TCoder>> => {
    if (options && options.body) {
      options.body = coder.encode(options.body)
    }

    return fetchFromApi(path, options)
      .then((response) => response.json())
      .then(coder.decode)
      .then((result) => {
        if (isLeft(result)) {
          console.error(
            `Error decoding ${path}`,
            PathReporter.report(result).join('\n')
          )
          throw PathReporter.report(result)
        }

        return result.right
      })
  }

  const fetchAndEncodeDecodeFromApi = async <
    TModelCoder extends ioTs.Type<any>,
    TInputCoder extends ioTs.Type<any>
  >(
    path: string,
    modelCoder: TModelCoder,
    inputCoder: TInputCoder,
    options?: { method?: string; body: ioTs.TypeOf<TInputCoder> }
  ): Promise<ioTs.TypeOf<TModelCoder>> => {
    if (options && options.body) {
      options.body = inputCoder.encode(options.body)
    }

    return fetchFromApi(path, options)
      .then((response) => response.json())
      .then(modelCoder.decode)
      .then((result) => {
        if (isLeft(result)) {
          throw PathReporter.report(result)
        }

        return result.right
      })
  }

  const fetchAndEncodeFromApi = async <TInputCoder extends ioTs.Type<any>>(
    path: string,
    inputCoder: TInputCoder,
    options?: { method?: string; body: ioTs.TypeOf<TInputCoder> }
  ): Promise<void> => {
    if (options && options.body) {
      options.body = inputCoder.encode(options.body)
    }

    return fetchFromApi(path, options).then((response) => {
      if (!response.ok) {
        throw new Error('Did not get a successful response')
      }
    })
  }

  // users

  const tikTok = {
    getLoginUrl: () =>
      fetchAndDecodeFromApi('/users/tiktok/oauth', OAuthLoginUrlCoder),
    unlink: () => fetchFromApi('/users/tiktok/unlink', { method: 'POST' }),
  }

  const instagram = {
    getLoginUrl: () =>
      fetchAndDecodeFromApi('/users/facebook/oauth', OAuthLoginUrlCoder),
    unlink: () => fetchFromApi('/users/facebook/unlink', { method: 'POST' }),
  }

  // bookmarks

  const getBookmark = (productId: number): Promise<Bookmark | null> =>
    fetchAndDecodeFromApi(
      `/bookmarks/product/${productId}`,
      ioTs.union([BookmarkCoder, ioTs.null])
    ).catch(() => null)

  const getBookmarks = (): Promise<Bookmark[]> =>
    fetchAndDecodeFromApi(`/bookmarks`, ioTs.array(BookmarkCoder))

  const createBookmark = (input: BookmarkInput): Promise<Bookmark> =>
    fetchAndEncodeDecodeFromApi(
      '/bookmarks',
      BookmarkCoder,
      BookmarkInputCoder,
      {
        method: 'POST',
        body: input,
      }
    )

  const deleteBookmark = (productId: number): Promise<void> =>
    fetchFromApi(`/bookmarks/product/${productId}`, { method: 'DELETE' }).then(
      () => {}
    )

  const getRoundup = (): Promise<Roundup> =>
    fetchAndDecodeFromApi('/bookmarks/roundup', RoundupCoder)

  const getRoundupProducts = () =>
    fetchAndDecodeFromApi(
      '/bookmarks/roundup_products',
      ioTs.array(ProductWithImageCoder)
    )

  const exportBookmarks = () => fetchFromApi('/bookmarks/export')

  const makeResource = <
    TModelCoder extends ioTs.Type<any>,
    TInputCoder extends ioTs.Type<any>
  >(
    name: string,
    modelCoder: TModelCoder,
    inputCoder: TInputCoder,
    options?: {
      getAllWithDateRange?: boolean
      search?: boolean
      exportCsv?: boolean
    }
  ): Resource<ioTs.TypeOf<TModelCoder>, ioTs.TypeOf<TInputCoder>> => ({
    get: (id: number) => fetchAndDecodeFromApi(`/${name}/${id}`, modelCoder),
    getAll: () => fetchAndDecodeFromApi(`/${name}`, ioTs.array(modelCoder)),
    getAllWithDateRange: options?.getAllWithDateRange
      ? (start, end, includeStats) => {
          const queryParams = queryParamsForDateRange(start, end, includeStats)

          return fetchAndDecodeFromApi(
            `/${name}?${queryParams}`,
            ioTs.array(modelCoder)
          )
        }
      : undefined,
    create: (input: ioTs.TypeOf<TInputCoder>) =>
      fetchAndEncodeDecodeFromApi(`/${name}`, modelCoder, inputCoder, {
        method: 'POST',
        body: inputCoder.encode(input),
      }),
    update: (id: number, input: ioTs.TypeOf<TInputCoder>) =>
      fetchAndEncodeDecodeFromApi(`/${name}/${id}`, modelCoder, inputCoder, {
        method: 'PUT',
        body: inputCoder.encode(input),
      }),
    delete: (id: number) =>
      fetchFromApi(`/${name}/${id}`, { method: 'DELETE' }).then(() => {}),
    search: options?.search
      ? (query: string) =>
          fetchAndDecodeFromApi(
            `/${name}/search?query=${encodeURIComponent(query)}`,
            ioTs.array(modelCoder)
          )
      : undefined,
    exportCsv: options?.exportCsv
      ? () => fetchFromApi(`/${name}/export`)
      : undefined,
  })

  const users = {
    getCurrent: () => fetchAndDecodeFromApi('/users/current', CurrentUserCoder),
    tikTok,
    instagram,
    validateUsername: (username: string) =>
      fetchAndDecodeFromApi(
        `/validations/username?username=${username}`,
        UsernameValidationCoder
      ),
    details: (username: string) =>
      fetchAndDecodeFromApi(`/users/${username}/details`, UserWithImagesCoder),
    userLists: (username: string) =>
      fetchAndDecodeFromApi(
        `/users/${username}/user_lists`,
        ioTs.array(UserListPublicCoder)
      ),
    feed: (usernameOrUserId: string | number) =>
      fetchAndDecodeFromApi(`/users/${usernameOrUserId}/feed`, UserFeedCoder),
    currentUserFeed: () =>
      fetchAndDecodeFromApi('/users/current/feed', UserFeedCoder),
    startOnboarding: () =>
      fetchFromApi('/users/current/start_onboarding', { method: 'POST' }),
    featured: () =>
      fetchAndDecodeFromApi('/users/featured', UsersWithImagesCoder),
    featuredFeed: () =>
      fetchAndDecodeFromApi(
        `/users/featured/feed?include_features=true`,
        FeaturedUsersFeedCoder
      ),
    triggerSocialMediaRefresh: (userId: number) =>
      fetchFromApi(`/users/${userId}/trigger_social_media_refresh`, {
        method: 'POST',
      }),
    ...makeResource('users', CmsUserCoder, UserInputCoder, {
      getAllWithDateRange: true,
      exportCsv: true,
    }),
  }

  const images = {
    presignedUrl: (input: ImagePresignedUrlInput) =>
      fetchAndEncodeDecodeFromApi(
        '/images/presigned_url',
        ImagePresignedUrlCoder,
        ImagePresignedUrlInputCoder,
        { method: 'POST', body: ImagePresignedUrlInputCoder.encode(input) }
      ),
    create: (input: ImageInput) =>
      fetchAndEncodeDecodeFromApi('/images', ImageCoder, ImageInputCoder, {
        method: 'POST',
        body: ImageInputCoder.encode(input),
      }),
    get: (id: number) => fetchAndDecodeFromApi(`/images/${id}`, ImageCoder),
  }

  const affiliateDomains = makeResource(
    'affiliate_domains',
    AffiliateDomainCoder,
    AffiliateDomainInputCoder
  )

  const brands = {
    ...makeResource('brands', BrandCoder, BrandInputCoder, {
      search: true,
      exportCsv: true,
    }),
    findOrCreateByName: (input: BrandInput) =>
      fetchAndEncodeDecodeFromApi(
        '/brands/find_or_create_by_name',
        BrandCoder,
        BrandInputCoder,
        { method: 'POST', body: BrandInputCoder.encode(input) }
      ),
    getAllUsed: () =>
      fetchAndDecodeFromApi('/brands/all_used', ioTs.array(BrandCoder)),
    mostRecentForUser: (userId: number) => {
      const queryParams = new URLSearchParams()
      queryParams.append('user_id', userId.toString())

      return fetchAndDecodeFromApi(
        `/brands/most_recent_for_user?${queryParams}`,
        ioTs.array(BrandCoder)
      )
    },
    searchUsed: (query: string) =>
      fetchAndDecodeFromApi(
        `/brands/search_used?query=${encodeURIComponent(query)}`,
        ioTs.array(BrandCoder)
      ),
    feed: (brandId: number) =>
      fetchAndDecodeFromApi(`/brands/${brandId}/feed`, BrandFeedCoder),
    productStats: (
      brandId: number,
      start: Date | undefined,
      end: Date | undefined,
      includeStats: boolean
    ) => {
      const queryParams = queryParamsForDateRange(start, end, includeStats)

      return fetchAndDecodeFromApi(
        `/brands/${brandId}/product_stats?${queryParams}`,
        ioTs.array(BrandProductStatsCoder)
      )
    },
    import: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)

      await fetch(`${API_ROOT}/brands/import`, {
        method: 'POST',
        headers: await headers(),
        body: formData,
      })
    },
  }

  const categories = {
    ...makeResource('categories', CategoryWithUserIdsCoder, CategoryInputCoder),
    featured: () =>
      fetchAndDecodeFromApi('/categories/featured', FeaturedCategoriesCoder),
    feed: (categoryId: number) =>
      fetchAndDecodeFromApi(
        `/categories/${categoryId}/feed`,
        CategoryFeedCoder
      ),
  }

  const config = {
    config: () => fetchAndDecodeFromApi('/config', ConfigCoder),
  }

  const brandFeatures = {
    ...makeResource(
      'brand_features',
      BrandFeatureCoder,
      BrandFeatureInputCoder
    ),
    featured: () =>
      fetchAndDecodeFromApi(
        `/brand_features/featured`,
        ioTs.array(BrandFeatureWithUsernameAndImageCoder)
      ),
  }

  const creatorFeatures = {
    ...makeResource(
      'creator_features',
      CreatorFeatureCoder,
      CreatorFeatureInputCoder
    ),
    featured: () =>
      fetchAndDecodeFromApi(
        `/creator_features/featured`,
        ioTs.array(CreatorFeatureWithUsernameAndImageCoder)
      ),
  }

  const productFeatures = {
    ...makeResource(
      'product_features',
      ProductFeatureCoder,
      ProductFeatureInputCoder
    ),
    featured: () =>
      fetchAndDecodeFromApi(
        `/product_features/featured`,
        ioTs.array(ProductWithImageCoder)
      ),
  }

  const edits = {
    ...makeResource('edits', EditCoder, EditInputCoder, {
      getAllWithDateRange: true,
    }),
    featuredEdits: () =>
      fetchAndDecodeFromApi('/edits/featured', ioTs.array(EditCoder)),
    exportCsv: () => fetchFromApi('/user_edits/export'),
    search: (query: string, userId?: number) => {
      const queryParams = new URLSearchParams()
      if (userId) {
        queryParams.append('user_id', userId.toString())
      }

      queryParams.append('query', query)
      return fetchAndDecodeFromApi(
        `/edits/search?${queryParams}`,
        ioTs.array(EditCoder)
      )
    },
  }

  const userLists = {
    ...makeResource('user_lists', UserListCoder, UserListInputCoder),
    updateProducts: (id: number, input: UserListProductsInput) =>
      fetchAndEncodeDecodeFromApi(
        `/user_lists/${id}/update_products`,
        UserListCoder,
        UserListProductsInputCoder,
        {
          method: 'PUT',
          body: UserListProductsInputCoder.encode(input),
        }
      ),
    feed: (userId?: number) =>
      fetchAndDecodeFromApi(
        `/user_lists/feed?${userEditsQueryParams({ userId })}`,
        UserListFeedCoder
      ),
    getForPublic: (id: number) =>
      fetchAndDecodeFromApi(`/user_lists/${id}`, UserListPublicCoder),
    editFeed: (editId: number) =>
      fetchAndDecodeFromApi(
        `/edits/${editId}/feed`,
        ioTs.array(UserListPublicCoder)
      ),
    search: (query: string, userId?: number) => {
      const queryParams = new URLSearchParams()

      if (userId) {
        queryParams.append('user_id', userId.toString())
      }

      queryParams.append('query', query)

      return fetchAndDecodeFromApi(
        `/user_lists/search?${queryParams}`,
        ioTs.array(UserListPublicCoder)
      )
    },
  }

  const userEditsQueryParams = (input: {
    userId?: number
    username?: string
    editId?: number
  }) => {
    const queryParams = new URLSearchParams()

    if (input.userId) {
      queryParams.append('user_id', input.userId.toString())
    }

    if (input.username) {
      queryParams.append('username', input.username)
    }

    if (input.editId) {
      queryParams.append('edit_id', input.editId.toString())
    }

    return queryParams
  }

  const userEdits = {
    get: (id: number, userId?: number) =>
      fetchAndDecodeFromApi(
        `/user_edits/${id}?${userEditsQueryParams({ userId })}`,
        UserEditCoder
      ),
    forUsernameAndEdit: (username: string, editId: number) =>
      fetchAndDecodeFromApi(
        `/user_edits/for_username_and_edit?${userEditsQueryParams({
          username,
          editId,
        })}`,
        UserEditPublicCoder
      ),
    update: (id: number, userId: number | undefined, input: UserEditInput) =>
      fetchAndEncodeDecodeFromApi(
        `/user_edits/${id}?${userEditsQueryParams({ userId })}`,
        UserEditCoder,
        UserEditInputCoder,
        {
          method: 'PUT',
          body: UserEditInputCoder.encode(input),
        }
      ),
    feed: (userId?: number) =>
      fetchAndDecodeFromApi(
        `/user_edits/feed?${userEditsQueryParams({ userId })}`,
        UserEditFeedCoder
      ),
    editFeed: (editId: number) =>
      fetchAndDecodeFromApi(
        `/edits/${editId}/feed`,
        ioTs.array(UserEditPublicCoder)
      ),
  }

  const queuedImages = makeResource(
    'queued_images',
    QueuedImageCoder,
    QueuedImageInputCoder
  )

  const products = {
    ...makeResource(
      'products',
      ProductWithImageAndRelatedProductIdsCoder,
      ProductInputCoder,
      {
        getAllWithDateRange: true,
        exportCsv: true,
      }
    ),
    details: (
      id: number,
      options?: {
        mediaId?: number
        userListId?: number
      }
    ) => {
      const queryParams = new URLSearchParams()

      if (options?.mediaId) {
        queryParams.append('media_id', options.mediaId.toString())
      }
      if (options?.userListId) {
        queryParams.append('user_list_id', options.userListId.toString())
      }

      return fetchAndDecodeFromApi(
        `/products/${id}/details?${queryParams}`,
        ProductDetailsCoder
      )
    },
    detailsBrandProducts: (id: number) =>
      fetchAndDecodeFromApi(
        `/products/${id}/details/brand_products`,
        ioTs.array(ProductWithImageCoder)
      ),
    detailsRelatedProducts: (
      id: number,
      options?: {
        random?: boolean
      }
    ) => {
      const queryParams = new URLSearchParams()

      if (options?.random) {
        queryParams.append('random', options.random.toString())
      }

      return fetchAndDecodeFromApi(
        `/products/${id}/details/related_products?${queryParams}`,
        ioTs.array(ProductWithImageCoder)
      )
    },
    suggestionFromProductUrl: (url: string) =>
      fetchAndDecodeFromApi(
        `/products/suggest/scrape?url=${encodeURIComponent(url)}`,
        ProductSuggestionResponseCoder
      ),
    queue: (userId?: number) => {
      const queryParams = new URLSearchParams()

      if (userId) {
        queryParams.append('user_id', userId.toString())
      }

      return fetchAndDecodeFromApi(
        `/products/queue?${queryParams}`,
        ioTs.array(ProductUrlCoder)
      )
    },
    detected: (content: MediaContent) => {
      const queryParams = new URLSearchParams()

      if (isImageMediaContent(content)) {
        queryParams.append('image_id', content.value.toString())
      } else {
        queryParams.append('video_id', content.value.toString())
      }

      return fetchAndDecodeFromApi(
        `/products/detected?${queryParams}`,
        ioTs.array(ioTs.array(DetectedProductCoder))
      )
    },
    import: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)

      await fetch(`${API_ROOT}/products/import`, {
        method: 'POST',
        headers: await headers(),
        body: formData,
      })
    },
    exportShopify: (url: string) =>
      fetchFromApi(`/products/export_shopify?url=${encodeURIComponent(url)}`),
    isAffiliatedProduct: (productUrl: string): Promise<IsAffiliatedProduct> =>
      fetchAndEncodeDecodeFromApi(
        '/products/is_affiliated_product',
        IsAffiliatedProductCoder,
        IsAffiliatedProductInputCoder,
        {
          method: 'POST',
          body: { productUrl },
        }
      ),
    searchUsedByUser: (query: string) =>
      fetchAndDecodeFromApi(
        `/products/search_used_by_user?query=${encodeURIComponent(query)}`,
        ioTs.array(ProductWithImageAndRelatedProductIdsCoder)
      ),
  }

  const videos = {
    presignedUrl: () =>
      fetchAndDecodeFromApi('/videos/presigned_url', VideoPresignedUrlCoder, {
        method: 'POST',
      }),
    playbackId: (uploadId: string) =>
      fetchAndDecodeFromApi(
        `/videos/playback_id/${uploadId}`,
        VideoPlaybackIdCoder
      ),
    create: (input: VideoInput) =>
      fetchAndEncodeDecodeFromApi('/videos', VideoCoder, VideoInputCoder, {
        method: 'POST',
        body: VideoInputCoder.encode(input),
      }),
    get: (id: number) => fetchAndDecodeFromApi(`/videos/${id}`, VideoCoder),
  }

  const mediaQueue = {
    index: (options?: { userId?: number; forceRefresh?: boolean }) => {
      const queryParams = new URLSearchParams()

      if (options?.userId) {
        queryParams.append('user_id', options.userId.toString())
      }

      if (options?.forceRefresh) {
        queryParams.append('force_refresh', 'true')
      }

      return fetchAndDecodeFromApi(
        `/media_queue?${queryParams}`,
        MediaQueueCoder
      )
    },
  }
  const colleges = {
    getAll: () => fetchAndDecodeFromApi(`/colleges`, ioTs.array(CollegeCoder)),
    get: (collegeId: number) =>
      fetchAndDecodeFromApi(`/colleges/${collegeId}`, CollegeCoder),
    getByShortname: (shortname: string) =>
      fetchAndDecodeFromApi(`/colleges/shortname/${shortname}`, CollegeCoder),
  }
  const creatorLaunchImages = {
    ...makeResource(
      'creator_launch_images',
      CreatorLaunchImageCoder,
      CreatorLaunchImageInputCoder
    ),
    latest: () =>
      fetchAndDecodeFromApi(
        '/creator_launch_images/latest',
        ioTs.union([CreatorLaunchImageWithImageCoder, ioTs.null])
      ),
  }

  const customLinks = {
    getForUserId: (userId: number) =>
      fetchAndDecodeFromApi(
        `/user_custom_links?user_id=${userId}`,
        ioTs.array(CustomLinkCoder)
      ),
    ...makeResource('user_custom_links', CustomLinkCoder, CustomLinkInputCoder),
  }

  const search = {
    search: (query: string) =>
      fetchAndDecodeFromApi(
        `/search?query=${encodeURIComponent(query)}`,
        ioTs.array(SearchResultCoder)
      ),
  }

  const follows = {
    ...makeResource('follows', FollowCoder, FollowInputCoder),
    getForCreatorId: (creatorId: number) =>
      fetchAndDecodeFromApi(
        `/follows/creators/${creatorId}`,
        ioTs.union([FollowCoder, ioTs.null])
      ),
    creators: () =>
      fetchAndDecodeFromApi('/follows/creators', UsersWithImagesCoder),
    feed: () => fetchAndDecodeFromApi(`/follows/feed`, FeaturedUsersFeedCoder),
  }

  const tags = {
    ...makeResource('tags', TagCoder, TagInputCoder),
    feed: (tagId: number) =>
      fetchAndDecodeFromApi(`/tags/${tagId}/feed`, TagFeedCoder),
    default: () =>
      fetchAndDecodeFromApi('/tags/default', ioTs.union([TagCoder, ioTs.null])),
  }

  const labels = {
    ...makeResource('labels', LabelCoder, LabelInputCoder),
    feed: (labelId: number) =>
      fetchAndDecodeFromApi(`/labels/${labelId}/feed`, LabelFeedCoder),
  }

  const messages = {
    create: (input: MessageInput) =>
      fetchAndEncodeFromApi('/messages', MessageInputCoder, {
        method: 'POST',
        body: input,
      }),
  }
  const media = {
    ...makeResource('media', MediaCoder, MediaInputCoder, {
      getAllWithDateRange: true,
      exportCsv: true,
    }),
    clearPositions: () =>
      fetchFromApi('/media/clear_positions', {
        method: 'POST',
      }),
    getFeedItem: (id: number) =>
      fetchAndDecodeFromApi(
        `/media/feed?media_id=${id}`,
        ioTs.array(FeedMediaCoder)
      ),
  }

  const analytics = {
    createMediaView: (input: Omit<MediaViewInput, 'source'>) =>
      fetchAndEncodeFromApi('/analytics/media_views', MediaViewInputCoder, {
        method: 'POST',
        body: {
          ...input,
          source: 'web',
        },
      }),
    createProductView: (input: Omit<ProductViewInput, 'analyticSource'>) =>
      fetchAndEncodeFromApi('/analytics/product_views', ProductViewInputCoder, {
        method: 'POST',
        body: {
          ...input,
          analyticSource: 'web',
        },
      }),
    createOutgoingProductClick: (
      input: Omit<OutgoingProductClickInput, 'analyticSource'>
    ) =>
      fetchAndEncodeFromApi(
        '/analytics/outgoing_product_clicks',
        OutgoingProductClickInputCoder,
        {
          method: 'POST',
          body: {
            ...input,
            analyticSource: 'web',
          },
        }
      ),
    createOutgoingClick: (input: Omit<OutgoingClickInput, 'source'>) =>
      fetchAndEncodeFromApi(
        '/analytics/outgoing_clicks',
        OutgoingClickInputCoder,
        {
          method: 'POST',
          body: {
            ...input,
            source: 'web',
          },
        }
      ),
    createBehindTheGateView: (input: Omit<BehindTheGateViewInput, 'source'>) =>
      fetchAndEncodeFromApi(
        '/analytics/behind_the_gate_views',
        BehindTheGateViewInputCoder,
        {
          method: 'POST',
          body: {
            ...input,
            source: 'web',
          },
        }
      ),
    createUserListView: (input: Omit<UserListViewInput, 'source'>) =>
      fetchAndEncodeFromApi(
        '/analytics/user_list_views',
        UserListViewInputCoder,
        {
          method: 'POST',
          body: {
            ...input,
            source: 'web',
          },
        }
      ),
    createEditFeedView: (input: Omit<EditFeedViewInput, 'source'>) =>
      fetchAndEncodeFromApi(
        '/analytics/edit_feed_views',
        EditFeedViewInputCoder,
        {
          method: 'POST',
          body: {
            ...input,
            source: 'web',
          },
        }
      ),
    createSearchResultClick: (input: SearchResultClickInput) =>
      fetchAndEncodeFromApi(
        '/analytics/search_result_clicks',
        SearchResultClickInputCoder,
        {
          method: 'POST',
          body: input,
        }
      ),
  }

  const socialMediaPosts = {
    delete: (id: number) =>
      fetchFromApi(`/social_media_posts/${id}`, { method: 'DELETE' }).then(
        () => {}
      ),
  }

  const auth = {
    createVerificationEmail: (input: VerifyEmailInput) =>
      fetchAndEncodeFromApi('/users/auth/register', VerifyEmailInputCoder, {
        method: 'POST',
        body: input,
      }),
    resetPasswordEmail: (input: VerifyEmailInput) =>
      fetchAndEncodeFromApi(
        '/users/auth/reset_password',
        VerifyEmailInputCoder,
        {
          method: 'POST',
          body: input,
        }
      ),
    logIn: (input: LoginInput) =>
      fetchAndEncodeDecodeFromApi(
        '/users/auth/login',
        LoginCoder,
        LoginInputCoder,
        {
          method: 'POST',
          body: input,
        }
      ),
    status: (email: string, source: EmailSource) => {
      const queryParams = new URLSearchParams()
      queryParams.append('email', email)
      queryParams.append('source', source)

      return fetchAndDecodeFromApi(
        `/users/auth/status?${queryParams}`,
        VerificationStatusCoder
      )
    },
  }

  const insightsQueryParams = (insightsSince?: string) => {
    const queryParams = new URLSearchParams()
    if (insightsSince) {
      queryParams.append('insights_since', insightsSince)
    }
    return queryParams
  }

  const insights = {
    get: (usernameOrUserId: string | number, insightsSince?: Date) =>
      fetchAndDecodeFromApi(
        `/users/${usernameOrUserId}/insights?${insightsQueryParams(
          insightsSince?.toISOString()
        )}`,
        InsightsCoder
      ),
  }

  const creatorEarnings = {
    getForDate: (date: string) =>
      fetchAndDecodeFromApi(
        `/creator_earnings/date/${date}`,
        ioTs.array(CreatorEarningsCoder)
      ),
    getForUser: (userId: string) =>
      fetchAndDecodeFromApi(
        `/creator_earnings/user/${parseInt(userId)}`,
        ioTs.array(CreatorEarningsCoder)
      ),
    overview: () =>
      fetchAndDecodeFromApi(
        '/creator_earnings/overview',
        ioTs.array(CreatorEarningsOverviewCoder)
      ),
    creatorOverview: (usernameOrUserId: string | number) =>
      fetchAndDecodeFromApi(
        `/creator_earnings/${usernameOrUserId}/creator_overview`,
        CreatorEarningsCreatorOverviewCoder
      ),
    updateManyUpdatedEarnings: (input: CreatorEarningsManyInput) =>
      fetchAndEncodeFromApi(
        '/creator_earnings/override_earnings',
        CreatorEarningsManyInputCoder,
        {
          method: 'PUT',
          body: input,
        }
      ),
    updateCostsAndEarnings: (input: CreatorFundCostsInput) =>
      fetchAndEncodeFromApi(
        '/creator_earnings/update_costs_and_earnings',
        CreatorFundCostsInputCoder,
        {
          method: 'PUT',
          body: input,
        }
      ),
    reset: (input: CreatorEarningsDateInput) =>
      fetchAndEncodeFromApi(
        '/creator_earnings/reset_date',
        CreatorEarningsDateInputCoder,
        {
          method: 'PUT',
          body: input,
        }
      ),
    generateDate: (input: CreatorEarningsDateInput) =>
      fetchAndEncodeFromApi(
        '/creator_earnings/generate_date',
        CreatorEarningsDateInputCoder,
        {
          method: 'PUT',
          body: input,
        }
      ),
    commitDate: (input: CreatorEarningsDateInput) =>
      fetchAndEncodeFromApi(
        '/creator_earnings/commit_date',
        CreatorEarningsDateInputCoder,
        {
          method: 'PUT',
          body: input,
        }
      ),
  }

  const creatorFundCosts = {
    mostRecent: () =>
      fetchAndDecodeFromApi(
        `/creator_fund_costs/most_recent`,
        CreatorFundCostsCoder
      ),
    create: (input: CreatorFundCostsInput): Promise<CreatorFundCosts> =>
      fetchAndEncodeDecodeFromApi(
        '/creator_fund_costs',
        CreatorFundCostsCoder,
        CreatorFundCostsInputCoder,
        {
          method: 'POST',
          body: input,
        }
      ),
  }

  const redemptionRequest = {
    ...makeResource(
      'redemption_requests',
      RedemptionRequestCoder,
      RedemptionRequestInputCoder
    ),
    create: (input: RedemptionRequestInput): Promise<RedemptionRequest> =>
      fetchAndEncodeDecodeFromApi(
        '/redemption_requests',
        RedemptionRequestCoder,
        RedemptionRequestInputCoder,
        {
          method: 'POST',
          body: input,
        }
      ),
    markAsDeleted: (id: number) =>
      fetchAndDecodeFromApi(
        `/redemption_requests/${id}/mark_as_deleted`,
        RedemptionRequestCoder,
        {
          method: 'PUT',
        }
      ),
  }

  const getStatus = () => fetchFromApi('/status')

  return {
    getStatus,

    getBookmark,
    getBookmarks,
    createBookmark,
    deleteBookmark,
    getRoundup,
    getRoundupProducts,
    exportBookmarks,

    affiliateDomains,
    analytics,
    brands,
    brandFeatures,
    categories,
    config,
    creatorEarnings,
    creatorFeatures,
    creatorFundCosts,
    colleges,
    edits,
    follows,
    images,
    insights,
    labels,
    media,
    mediaQueue,
    messages,
    products,
    productFeatures,
    queuedImages,
    videos,
    creatorLaunchImages,
    customLinks,
    search,
    socialMediaPosts,
    tags,
    userEdits,
    users,
    auth,
    redemptionRequest,
    userLists,
  }
}

export type Api = ReturnType<typeof makeApi>

type AddApi = SimplePropsAdder<{ api: Api }>

export const addApi: AddApi = flowMax(
  addProps(
    () => ({
      api: makeApi(),
    }),
    []
  )
)

interface ConfigContext {
  config: Config
}

export const [addConfigContextProviderInternal, addConfig] =
  getContextHelpers<ConfigContext>(toObjectKeys(['config']))

export const addConfigContextProvider: SimpleUnchangedProps = flowMax(
  addApi,
  addState('config', 'setConfig', typedAs<Config | null>(null)),
  addHandlers({
    refetchConfig:
      ({ api, setConfig }) =>
      () =>
        api.config.config().then(setConfig),
  }),
  addEffectOnMount(({ refetchConfig }) => () => {
    refetchConfig()
  }),
  branchIfNullish('config'),
  addConfigContextProviderInternal
)

interface UserContext {
  user: CurrentUser
  refetchUser: () => void
}

export const [addUserContextProviderInternal, addUser] =
  getContextHelpers<UserContext>(toObjectKeys(['user', 'refetchUser']))

export const addUserContextProvider: SimpleUnchangedProps = flowMax(
  authenticate,
  addAuthContext,
  addApi,
  addState('user', 'setUser', typedAs<CurrentUser | null>(null)),
  addNavigate,
  addHandlers({
    refetchUser:
      ({ api, setUser, navigate, invalidateCredentials }) =>
      () =>
        api.users
          .getCurrent()
          .then(setUser)
          .catch(() => {
            invalidateCredentials()
            navigate('/')
          }),
  }),
  addEffectOnMount(({ refetchUser }) => () => {
    refetchUser()
  }),
  branchIfNullish('user'),
  addUserContextProviderInternal
)

interface ShopperUserContext {
  user: CurrentUser | null
  refetchUser: () => void
}

export const [addShopperUserContextProviderInternal, addShopperUser] =
  getContextHelpers<ShopperUserContext>(toObjectKeys(['user', 'refetchUser']))

export const addShopperUserContextProvider: SimpleUnchangedProps = flowMax(
  addApi,
  addState('user', 'setUser', typedAs<CurrentUser | null>(null)),
  addAuthContext,
  addHandlers({
    refetchUser:
      ({ api, setUser, invalidateCredentials }) =>
      () =>
        api.users
          .getCurrent()
          .then((user) => {
            setUser(user)
          })
          .catch(() => invalidateCredentials()),
  }),
  addEffect(
    ({ refetchUser }) =>
      () => {
        refetchUser()
      },
    ['isAuthenticated']
  ),
  addEffectOnMount(({ refetchUser }) => () => {
    refetchUser()
  }),
  addShopperUserContextProviderInternal
)

type AddUserOrRedirect = typeof addUser

export const addAdminOrRedirect: AddUserOrRedirect = flowMax(
  addUser,
  addNavigate,
  addEffectOnMount(({ user, navigate }) => () => {
    if (!user.isAdmin) {
      navigate('/')
    }
  })
)

const SHOPPER_URL = getEnvVar('SHOPPER_URL')

export const addAdminOrCreatorOrRedirect: AddUserOrRedirect = flowMax(
  addUser,
  addNavigate,
  addEffectOnMount(({ user }) => () => {
    if (!user.isAdmin && !user.isCreator) {
      window.location.href = SHOPPER_URL
    }
  })
)
